import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ToastMessageMeta } from '@/libs/tanstack-query/TanStackQuery'
import { getErrorMessage } from './api.utils'

const resolveMeta = (
  meta: ToastMessageMeta | undefined,
  variables: unknown,
): ResolvedToastMessageMeta | undefined =>
  meta && {
    ...meta,
    toastMessageError:
      typeof meta.toastMessageError === 'function'
        ? meta.toastMessageError(variables)
        : meta.toastMessageError,
    toastMessageSuccess:
      typeof meta.toastMessageSuccess === 'function'
        ? meta.toastMessageSuccess(variables)
        : meta.toastMessageSuccess,
  }

type ResolvedToastMessageMeta = Omit<
  ToastMessageMeta,
  'toastMessageError' | 'toastMessageSuccess'
> & {
  toastMessageError?: string
  toastMessageSuccess?: string
}

export const handleApiError = (error: any, meta?: ResolvedToastMessageMeta) => {
  if (meta?.toastMessageError) {
    const skipCodes = meta.skipToastErrorCodes as string[] | undefined

    if (skipCodes?.includes(error?.code)) {
      return
    }

    const message = getErrorMessage(error, meta.toastMessageError)

    toast.error(message)
  }
}

export const handleApiSuccess = (meta?: ResolvedToastMessageMeta) => {
  if (meta?.toastMessageSuccess) {
    toast.success(meta.toastMessageSuccess)
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, { meta }) =>
      handleApiError(error, meta as ResolvedToastMessageMeta),
    onSuccess: (_data, { meta }) =>
      handleApiSuccess(meta as ResolvedToastMessageMeta),
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, _context, { meta }) =>
      handleApiError(error, resolveMeta(meta, variables)),
    onSuccess: (_data, variables, _context, { meta }) =>
      handleApiSuccess(resolveMeta(meta, variables)),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000, // 30 minutes
      retry: false,
      retryOnMount: false,
    },
    mutations: { retry: false },
  },
})
